import { ArrowLeft, ArrowRight } from "lucide-react";
import type { RefObject } from "react";
import { useRef } from "react";
import { brand } from "../config/brand";
import { categoryTiles } from "../config/catalog";
import { useCatalog } from "../hooks/useCatalog";
import { navigateTo } from "../lib/navigation";
import { Button } from "../components/Button";
import { ProductCard } from "../components/ProductCard";
import { SectionHeader } from "../components/SectionHeader";
import { SmartLink } from "../components/SmartLink";

export function HomePage() {
  const { menu } = useCatalog();
  const featuredRef = useRef<HTMLDivElement>(null);
  const dinnerRef = useRef<HTMLDivElement>(null);
  const featured = menu.filter((item) => item.featured);
  const dinner = menu.filter((item) => item.category === "dinner plates");

  function scrollRail(ref: RefObject<HTMLDivElement | null>, direction: -1 | 1) {
    const rail = ref.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.75, 240), behavior: "smooth" });
  }

  return (
    <>
      <section className="hero-section">
        <img src="/assets/site/hero-dining-table.jpg" alt="Private dining table at OJ's Bistro" />
        <div className="hero-panel">
          <h1>{brand.heroTitle}</h1>
          <p>{brand.heroBody}</p>
          <div className="hero-actions">
            <Button onClick={() => navigateTo("/menus")}>See today's menu</Button>
            <Button variant="secondary" onClick={() => navigateTo("/private-chef")}>
              Hire a private chef
            </Button>
          </div>
        </div>
      </section>

      <section className="content-section" id="today">
        <SectionHeader title="Today's Bistro Menu" action={{ label: "View full menu", href: "/menus" }} />
        <div className="rail-wrap">
          <button className="rail-arrow" type="button" aria-label="Previous menu items" onClick={() => scrollRail(featuredRef, -1)}>
            <ArrowLeft size={18} />
          </button>
          <div className="menu-rail five-up" ref={featuredRef}>
            {featured.map((item) => (
              <ProductCard key={item.id} item={item} />
            ))}
          </div>
          <button className="rail-arrow" type="button" aria-label="Next menu items" onClick={() => scrollRail(featuredRef, 1)}>
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <section className="content-section tinted" id="dinner">
        <SectionHeader title="Dinner Plates" action={{ label: "View all dinner plates", href: "/menus?category=dinner" }} />
        <div className="rail-wrap">
          <button className="rail-arrow" type="button" aria-label="Previous dinner plates" onClick={() => scrollRail(dinnerRef, -1)}>
            <ArrowLeft size={18} />
          </button>
          <div className="menu-rail four-up round-rail" ref={dinnerRef}>
            {dinner.map((item) => (
              <ProductCard key={item.id} item={item} circular compact />
            ))}
          </div>
          <button className="rail-arrow" type="button" aria-label="Next dinner plates" onClick={() => scrollRail(dinnerRef, 1)}>
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <section className="content-section" id="categories">
        <SectionHeader title="Explore By Category" centered />
        <div className="category-grid">
          {categoryTiles.map((tile) => (
            <SmartLink key={tile.id} href={tile.href} className="category-tile">
              <img src={tile.image} alt={tile.title} loading="lazy" />
              <h3>{tile.title}</h3>
              <p>{tile.body}</p>
              <span>Explore {tile.title}</span>
            </SmartLink>
          ))}
        </div>
      </section>

      <section className="quote-banner">
        <img src="/assets/site/chef-quote-banner.jpg" alt="Chef in a warm kitchen" />
        <div>
          <blockquote>“{brand.quote}”</blockquote>
          <p>Chef OJ</p>
        </div>
      </section>
    </>
  );
}
